/**
 * Splash-page markup mode: an authoring overlay (activated via the
 * {@link MARKUP_PARAM} query param, see `PaiSplashPage`) that lets a PAI
 * admin scribble pen strokes and drop text boxes directly onto the live
 * splash page. Annotations autosave to the Firestore document at
 * {@link MARKUP_DOC_PATH}, which is publicly readable and writable by pai1
 * admins/instructors under the existing `course/{courseId}/info/{infoId}`
 * rules — no rules changes required. A later tool will read that document
 * and turn the annotations into permanent splash-page content.
 *
 * Coordinates are stored in "page units": 1/1000ths of the splash page's
 * rendered width, for both axes, so annotations scale with the page and
 * keep their aspect ratio across viewport sizes.
 *
 * Annotations live on two independent layers — desktop and mobile — so the
 * page can be marked up differently per form factor. The toolbar toggles
 * which layer is being edited; it defaults to the layer matching the
 * viewport width at mount (see {@link MOBILE_MAX_WIDTH_PX}).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import styled from 'styled-components';
import {
  FaArrowsAlt,
  FaDesktop,
  FaEraser,
  FaFont,
  FaMobileAlt,
  FaPen,
  FaTrash,
  FaUndo,
} from 'react-icons/fa';
import {firestore} from '@cip/webapp/firebaseApp';
// The same font choices the slide editor's ribbon offers, reused so splash
// annotations and slides stay in lockstep.
import {SLIDE_FONTS} from 'slides/editor/FontFamilyControl';

/** Query param that turns markup mode on (presence is enough): `?markup`. */
export const MARKUP_PARAM = 'markup';

/**
 * Firestore document holding the splash annotations. Lives under the pai1
 * course's public `info` subcollection: world-readable, admin/instructor
 * writable (see firestore/default/firestore.rules, `/course/{c}/info/{i}`).
 */
const MARKUP_DOC_PATH = 'course/pai1/info/splashMarkup';

/** Both axes are stored as 1/UNITS fractions of the page's rendered width. */
const UNITS = 1000;

/** How long after the last edit the autosave fires, in ms. */
const AUTOSAVE_DELAY_MS = 900;

/** Chalk-style palette that reads on both the dark hero and light sections. */
const COLORS = [
  '#ffffff',
  '#ffd166',
  '#ef476f',
  '#06d6a0',
  '#4cc9f0',
  '#1a1a1a',
] as const;

/** Selectable pen stroke widths, in page units. */
const PEN_WIDTHS = [3, 6, 12] as const;

/** Default text size for new text boxes, in page units. */
const DEFAULT_TEXT_SIZE = 22;

/** The selection outline/handle blue, matching the slide editor's. */
const SELECTION_BLUE = '#2563eb';

/** Smallest a text box can be resized to, per axis, in page units. */
const MIN_TEXT_BOX_UNITS = 12;

/** How much the toolbar's A−/A+ steppers change the font size, in page units. */
const TEXT_SIZE_STEP = 2;

/** A point in page units. */
interface MarkupPoint {
  /** Horizontal position, in 1/UNITS of page width. */
  x: number;
  /** Vertical position, in 1/UNITS of page width. */
  y: number;
}

/** A freehand pen stroke. */
interface MarkupStroke {
  kind: 'stroke';
  /** Stable id, unique within the document. */
  id: string;
  /** CSS color of the stroke. */
  color: string;
  /** Stroke width in page units. */
  width: number;
  /** Polyline vertices in page units, in draw order. */
  points: MarkupPoint[];
}

/** A positioned text box. */
interface MarkupText {
  kind: 'text';
  /** Stable id, unique within the document. */
  id: string;
  /** CSS color of the text. */
  color: string;
  /** Font size in page units. */
  size: number;
  /** Top-left corner, in page units. */
  x: number;
  /** Top-left corner, in page units. */
  y: number;
  /** The text content (may contain newlines). */
  text: string;
  /** Box width in page units. Omitted: the box hugs its content. */
  w?: number;
  /** Box height in page units. Omitted: the box hugs its content. */
  h?: number;
  /**
   * CSS font-family, one of the slide editor's `SLIDE_FONTS` values. Omitted
   * or empty: the default splash annotation face (KaTeX_Main).
   */
  font?: string;
}

/** Any single annotation on the page. */
type MarkupItem = MarkupStroke | MarkupText;

/** The two independent annotation layers, keyed by form factor. */
type LayerId = 'desktop' | 'mobile';

/** The annotations of both layers, always saved and loaded together. */
type MarkupLayers = Record<LayerId, MarkupItem[]>;

/**
 * Widest viewport that defaults to editing the mobile layer, in px. The
 * boundary is inclusive: widths in the closed interval [0, this value] start
 * on the mobile layer; anything wider starts on desktop.
 */
const MOBILE_MAX_WIDTH_PX = 768;

/** The active authoring tool. */
type Tool = 'pen' | 'text' | 'move' | 'erase';

/** Autosave lifecycle, surfaced in the toolbar status pill. */
type SaveState = 'loading' | 'saved' | 'dirty' | 'saving' | 'error';

let idCounter = 0;

/** Returns a new id unique within this session's document. */
function newId(): string {
  idCounter += 1;
  return `m${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Narrows an unknown value to a finite number. */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Narrows an unknown value to a {@link MarkupPoint}. */
function isMarkupPoint(value: unknown): value is MarkupPoint {
  if (typeof value !== 'object' || value === null) return false;
  const record: Record<string, unknown> = {...value};
  return isFiniteNumber(record.x) && isFiniteNumber(record.y);
}

/**
 * Validates one stored item from Firestore.
 *
 * @param value Untrusted item from the loaded document.
 * @return The item as a typed {@link MarkupItem}, or null if malformed.
 */
function parseItem(value: unknown): MarkupItem | null {
  if (typeof value !== 'object' || value === null) return null;
  const record: Record<string, unknown> = {...value};
  if (typeof record.id !== 'string' || typeof record.color !== 'string') {
    return null;
  }
  if (record.kind === 'stroke') {
    if (!isFiniteNumber(record.width) || !Array.isArray(record.points)) {
      return null;
    }
    if (!record.points.every(isMarkupPoint)) return null;
    return {
      kind: 'stroke',
      id: record.id,
      color: record.color,
      width: record.width,
      points: record.points,
    };
  }
  if (record.kind === 'text') {
    if (
      !isFiniteNumber(record.size) ||
      !isFiniteNumber(record.x) ||
      !isFiniteNumber(record.y) ||
      typeof record.text !== 'string'
    ) {
      return null;
    }
    const text: MarkupText = {
      kind: 'text',
      id: record.id,
      color: record.color,
      size: record.size,
      x: record.x,
      y: record.y,
      text: record.text,
    };
    // Optional layout/font fields: assign only when valid, so the object
    // never carries `undefined` values (Firestore rejects them on save).
    if (isFiniteNumber(record.w)) text.w = record.w;
    if (isFiniteNumber(record.h)) text.h = record.h;
    if (typeof record.font === 'string') text.font = record.font;
    return text;
  }
  return null;
}

/** Validates an unknown value into a list of items, dropping malformed ones. */
function parseItemArray(value: unknown): MarkupItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseItem)
    .filter((item): item is MarkupItem => item !== null);
}

/**
 * Validates the loaded Firestore document into per-layer item lists, dropping
 * (not throwing on) anything malformed so one bad item can't take down the
 * whole overlay. Understands both the current version-2 shape
 * (`{layers: {desktop, mobile}}`) and the legacy version-1 shape (`{items}`),
 * whose items load into the desktop layer.
 *
 * @param data Untrusted document data from Firestore, or undefined when the
 *   document does not exist yet — empty layers are returned.
 * @return The valid annotations of each layer, in stored order.
 */
function parseStoredLayers(data: unknown): MarkupLayers {
  const empty: MarkupLayers = {desktop: [], mobile: []};
  if (typeof data !== 'object' || data === null) return empty;
  const record: Record<string, unknown> = {...data};
  if (typeof record.layers === 'object' && record.layers !== null) {
    const layers: Record<string, unknown> = {...record.layers};
    return {
      desktop: parseItemArray(layers.desktop),
      mobile: parseItemArray(layers.mobile),
    };
  }
  return {...empty, desktop: parseItemArray(record.items)};
}

/** Builds the SVG path string for a stroke's polyline. */
function strokePath(points: MarkupPoint[]): string {
  if (points.length === 0) return '';
  const [first, ...rest] = points;
  return (
    `M ${first.x.toFixed(1)} ${first.y.toFixed(1)}` +
    rest.map(p => ` L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join('')
  );
}

// ── Styled components ─────────────────────────────────────────────────────

const Wrapper = styled.div`
  position: relative;
`;

const Overlay = styled.div<{$cursor: string}>`
  position: absolute;
  inset: 0;
  z-index: 5000;
  cursor: ${p => p.$cursor};
  /* Pointer (not touch) gestures drive the pen, so suppress scrolling only
     while a pointer is down; wheel scrolling still passes through. */
  touch-action: none;
`;

const StrokeSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

/**
 * Positions one text annotation and (when selected) draws its selection
 * outline — the equivalent of the slide editor's `ElementBox` frame. Sizes
 * itself to its content when the annotation has no explicit `w`/`h` (inline
 * `width`/`height` are set instead once the author resizes it).
 */
const TextFrame = styled.div<{$selected: boolean}>`
  position: absolute;
  outline: ${p => (p.$selected ? `2px solid ${SELECTION_BLUE}` : 'none')};
`;

const TextItemBox = styled.div<{$interactive: boolean}>`
  pointer-events: ${p => (p.$interactive ? 'auto' : 'none')};
  white-space: pre-wrap;
  font-family: 'KaTeX_Main', Georgia, serif;
  line-height: 1.25;
  user-select: none;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
`;

const TextEditor = styled.textarea`
  display: block;
  background: rgba(0, 0, 0, 0.25);
  border: 1px dashed rgba(255, 255, 255, 0.6);
  border-radius: 4px;
  outline: none;
  resize: none;
  overflow: hidden;
  padding: 0;
  font-family: 'KaTeX_Main', Georgia, serif;
  line-height: 1.25;
`;

/** One resize-handle dot — same square/blue styling as the slide editor's. */
const HandleDot = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  background: ${SELECTION_BLUE};
  border: 1px solid white;
  border-radius: 2px;
`;

const Toolbar = styled.div`
  position: fixed;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(20, 20, 24, 0.88);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.14);
`;

const ToolButton = styled.button<{$active?: boolean}>`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font-size: 15px;
  color: ${p => (p.$active ? '#111' : '#eee')};
  background: ${p => (p.$active ? '#ffd166' : 'rgba(255, 255, 255, 0.08)')};
  transition:
    background 0.15s,
    transform 0.1s;

  &:hover {
    transform: scale(1.08);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:disabled:hover {
    transform: none;
  }
`;

const Swatch = styled.button<{$color: string; $active: boolean}>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  background: ${p => p.$color};
  border: 2px solid
    ${p => (p.$active ? '#ffd166' : 'rgba(255, 255, 255, 0.25)')};
  transform: ${p => (p.$active ? 'scale(1.2)' : 'scale(1)')};
  transition: transform 0.12s;
`;

const WidthDot = styled.button<{$px: number; $active: boolean}>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${p => (p.$active ? 'rgba(255, 209, 102, 0.25)' : 'transparent')};

  &::after {
    content: '';
    width: ${p => p.$px}px;
    height: ${p => p.$px}px;
    border-radius: 50%;
    background: #eee;
  }
`;

const FontSelect = styled.select`
  background: rgba(255, 255, 255, 0.08);
  color: #eee;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 12px;
  cursor: pointer;

  option {
    /* Options render with the OS's native menu chrome, so force a light
       background — the toolbar's dark styles don't reach into it and a
       default-white background disappears the toolbar's own #eee text. */
    background: #fff;
    color: #111;
  }
`;

const SizeButton = styled(ToolButton)`
  width: 28px;
  height: 28px;
  font-size: 12px;
  font-weight: 700;
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.18);
`;

/**
 * Fixed width for the status pill, sized to the longest label
 * ("Save failed", now that the full explanation moved to its `title`
 * tooltip — see `statusLabel`/`statusTitle`). A fixed width keeps the
 * toolbar's total size constant across save states; without it, the
 * toolbar (centered via `left: 50%; transform: translateX(-50%)`) visibly
 * shifts sideways every time the label's text changes length.
 */
const STATUS_PILL_WIDTH_PX = 96;

const StatusPill = styled.span<{$tone: 'ok' | 'busy' | 'bad'}>`
  box-sizing: border-box;
  width: ${STATUS_PILL_WIDTH_PX}px;
  font-family: 'Jost', sans-serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  padding: 4px 6px;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  color: ${p =>
    p.$tone === 'bad' ? '#ff8fa3' : p.$tone === 'busy' ? '#ffd166' : '#9be9c7'};
  background: rgba(255, 255, 255, 0.07);
`;

// ── Component ─────────────────────────────────────────────────────────────

/** Cursor shown over the page for each tool. */
const TOOL_CURSORS: Record<Tool, string> = {
  pen: 'crosshair',
  text: 'text',
  move: 'grab',
  erase: 'not-allowed',
};

/**
 * The eight resize handles of a selected text box — identical set, cursors,
 * and visual style to the slide editor's `ElementBox`.
 */
type ResizeHandle =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left';

const RESIZE_HANDLES: ResizeHandle[] = [
  'top-left',
  'top',
  'top-right',
  'right',
  'bottom-right',
  'bottom',
  'bottom-left',
  'left',
];

const CURSOR_BY_HANDLE: Record<ResizeHandle, string> = {
  'top-left': 'nwse-resize',
  top: 'ns-resize',
  'top-right': 'nesw-resize',
  right: 'ew-resize',
  'bottom-right': 'nwse-resize',
  bottom: 'ns-resize',
  'bottom-left': 'nesw-resize',
  left: 'ew-resize',
};

/** Absolute-position offsets (relative to the box) for each handle's dot. */
const HANDLE_POSITION: Record<ResizeHandle, CSSProperties> = {
  'top-left': {top: -6, left: -6},
  top: {top: -6, left: '50%', transform: 'translateX(-50%)'},
  'top-right': {top: -6, right: -6},
  right: {top: '50%', right: -6, transform: 'translateY(-50%)'},
  'bottom-right': {bottom: -6, right: -6},
  bottom: {bottom: -6, left: '50%', transform: 'translateX(-50%)'},
  'bottom-left': {bottom: -6, left: -6},
  left: {top: '50%', left: -6, transform: 'translateY(-50%)'},
};

/** A text box's resolved rectangle, in page units. */
interface UnitRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Live pointer-gesture state: dragging an item, or resizing a text box. */
type DragState =
  | {
      kind: 'move';
      /** Id of the item being dragged. */
      id: string;
      /** Pointer position when the drag began, in page units. */
      start: MarkupPoint;
      /** Snapshot of the item when the drag began. */
      original: MarkupItem;
    }
  | {
      kind: 'resize';
      /** Id of the text box being resized. */
      id: string;
      /** Which handle was grabbed. */
      handle: ResizeHandle;
      /** Pointer position when the resize began, in page units. */
      start: MarkupPoint;
      /** The box's rectangle when the resize began. */
      orig: UnitRect;
    };

/**
 * The markup-mode wrapper. Renders `children` (the real splash page)
 * untouched underneath an annotation overlay plus a floating toolbar.
 * Annotations load from and autosave to {@link MARKUP_DOC_PATH}; saving
 * requires the visitor to be signed in as a pai1 admin/instructor (the
 * toolbar surfaces write failures).
 *
 * @param children The splash page content to annotate.
 * @return The wrapped page with the authoring overlay on top.
 * @throws Never — load/save failures surface in the toolbar status pill.
 */
export function SplashMarkup({children}: {children: ReactNode}) {
  const [layers, setLayers] = useState<MarkupLayers>({
    desktop: [],
    mobile: [],
  });
  const [activeLayer, setActiveLayer] = useState<LayerId>(() =>
    window.innerWidth <= MOBILE_MAX_WIDTH_PX ? 'mobile' : 'desktop',
  );
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState<string>(COLORS[1]);
  const [penWidth, setPenWidth] = useState<number>(PEN_WIDTHS[1]);
  // Font applied to newly created text boxes; '' is SLIDE_FONTS' "Default"
  // sentinel (renders in the splash's own KaTeX_Main face).
  const [textFont, setTextFont] = useState<string>('');
  const [saveState, setSaveState] = useState<SaveState>('loading');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [liveStroke, setLiveStroke] = useState<MarkupStroke | null>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const undoStackRef = useRef<MarkupLayers[]>([]);
  const loadedRef = useRef(false);
  // One-shot flag: the next layers change is the initial load being applied,
  // not an edit, so the autosave effect should ignore it.
  const skipAutosaveRef = useRef(false);

  /** The annotations of the layer currently being edited. */
  const items = layers[activeLayer];

  /** Applies an updater to the items of the layer currently being edited. */
  const setActiveItems = useCallback(
    (updater: (current: MarkupItem[]) => MarkupItem[]) => {
      setLayers(current => ({
        ...current,
        [activeLayer]: updater(current[activeLayer]),
      }));
    },
    [activeLayer],
  );

  // Track the splash page's rendered size so page units map to pixels.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setPageWidth(el.clientWidth);
      setPageHeight(el.clientHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Load any previously saved annotations once on mount.
  useEffect(() => {
    let cancelled = false;
    firestore
      .doc(MARKUP_DOC_PATH)
      .get()
      .then(snapshot => {
        if (cancelled) return;
        const stored = parseStoredLayers(snapshot.data());
        // Prepend stored items so anything drawn while the load was still in
        // flight is kept rather than overwritten. Applying the loaded items
        // must not itself mark the document dirty.
        setLayers(current => {
          // Idempotent ref write (safe under StrictMode double-invoke): only
          // skip the autosave when nothing was drawn while loading.
          skipAutosaveRef.current =
            current.desktop.length === 0 && current.mobile.length === 0;
          return {
            desktop: [...stored.desktop, ...current.desktop],
            mobile: [...stored.mobile, ...current.mobile],
          };
        });
        loadedRef.current = true;
        setSaveState('saved');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.debug('[SplashMarkup] load failed', e);
        // Still allow authoring; the first save will surface auth problems.
        loadedRef.current = true;
        setSaveState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced autosave whenever any layer changes after the initial load.
  useEffect(() => {
    if (!loadedRef.current) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    setSaveState('dirty');
    const timer = setTimeout(() => {
      setSaveState('saving');
      firestore
        .doc(MARKUP_DOC_PATH)
        .set({version: 2, layers, updatedAtMs: Date.now()})
        .then(() => setSaveState('saved'))
        .catch((e: unknown) => {
          console.debug('[SplashMarkup] save failed', e);
          setSaveState('error');
        });
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [layers]);

  /** Snapshots both layers so the next mutation is undoable. */
  const pushUndo = useCallback(() => {
    undoStackRef.current.push(layers);
    if (undoStackRef.current.length > 100) undoStackRef.current.shift();
  }, [layers]);

  const undo = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (previous === undefined) return;
    setEditingId(null);
    setLayers(previous);
  }, []);

  // Cmd/Ctrl+Z anywhere on the page (except while typing in a text box).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isUndoChord = (e.metaKey || e.ctrlKey) && e.key === 'z';
      if (!isUndoChord || editingId !== null) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, editingId]);

  /** Converts a pointer event to page units relative to the overlay. */
  const toUnits = useCallback((e: ReactPointerEvent): MarkupPoint => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return {x: 0, y: 0};
    return {
      x: ((e.clientX - rect.left) / rect.width) * UNITS,
      y: ((e.clientY - rect.top) / rect.width) * UNITS,
    };
  }, []);

  /**
   * Closes any open text editor, deleting the text box if it was left empty
   * (its content is already in state via the editor's onChange).
   */
  const finishEditing = () => {
    if (editingId === null) return;
    setActiveItems(current =>
      current.filter(
        i => !(i.id === editingId && i.kind === 'text' && i.text.trim() === ''),
      ),
    );
    setEditingId(null);
  };

  const onOverlayPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // Cancel the browser's default mousedown focus change: without this, a
    // freshly mounted text editor is focused and then immediately blurred
    // (and, being empty, deleted) before it is ever visible.
    e.preventDefault();
    // Commit any in-progress text edit before starting a new gesture.
    finishEditing();
    // Clicking blank overlay space (not an item — those call stopPropagation)
    // deselects whatever text box was selected.
    setSelectedId(null);
    const point = toUnits(e);
    overlayRef.current?.setPointerCapture(e.pointerId);
    if (tool === 'pen') {
      setLiveStroke({
        kind: 'stroke',
        id: newId(),
        color,
        width: penWidth,
        points: [point],
      });
      return;
    }
    if (tool === 'text') {
      pushUndo();
      const id = newId();
      const newText: MarkupText = {
        kind: 'text',
        id,
        color,
        size: DEFAULT_TEXT_SIZE,
        x: point.x,
        y: point.y,
        text: '',
      };
      // '' is the "Default" sentinel — omit the field entirely rather than
      // storing an empty override.
      if (textFont !== '') newText.font = textFont;
      setActiveItems(current => [...current, newText]);
      setSelectedId(id);
      setEditingId(id);
    }
  };

  const onOverlayPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const point = toUnits(e);
    if (liveStroke !== null) {
      setLiveStroke(current => {
        if (current === null) return null;
        const last = current.points[current.points.length - 1];
        // Skip sub-2-unit jitter to keep stored strokes compact.
        if (Math.hypot(point.x - last.x, point.y - last.y) < 2) return current;
        return {...current, points: [...current.points, point]};
      });
      return;
    }
    const drag = dragRef.current;
    if (drag === null) return;
    const dx = point.x - drag.start.x;
    const dy = point.y - drag.start.y;
    if (drag.kind === 'resize') {
      // Grow/shrink the grabbed edges, keeping the opposite edges anchored —
      // same semantics as the slide editor's ElementBox.
      const {orig, handle} = drag;
      let {x, y, w, h} = orig;
      if (handle.includes('right')) {
        w = Math.max(MIN_TEXT_BOX_UNITS, orig.w + dx);
      }
      if (handle.includes('left')) {
        w = Math.max(MIN_TEXT_BOX_UNITS, orig.w - dx);
        x = orig.x + orig.w - w;
      }
      if (handle.includes('bottom')) {
        h = Math.max(MIN_TEXT_BOX_UNITS, orig.h + dy);
      }
      if (handle.includes('top')) {
        h = Math.max(MIN_TEXT_BOX_UNITS, orig.h - dy);
        y = orig.y + orig.h - h;
      }
      setActiveItems(current =>
        current.map(item =>
          item.id === drag.id && item.kind === 'text'
            ? {...item, x, y, w, h}
            : item,
        ),
      );
      return;
    }
    setActiveItems(current =>
      current.map(item => {
        if (item.id !== drag.id) return item;
        if (drag.original.kind === 'text') {
          return {
            ...drag.original,
            x: drag.original.x + dx,
            y: drag.original.y + dy,
          };
        }
        return {
          ...drag.original,
          points: drag.original.points.map(p => ({x: p.x + dx, y: p.y + dy})),
        };
      }),
    );
  };

  const onOverlayPointerUp = () => {
    if (liveStroke !== null) {
      if (liveStroke.points.length >= 2) {
        pushUndo();
        const committed = liveStroke;
        setActiveItems(current => [...current, committed]);
      }
      setLiveStroke(null);
    }
    dragRef.current = null;
  };

  /** Pointer-down on an existing item: drag (move), delete (erase), or edit. */
  const onItemPointerDown = (e: ReactPointerEvent, item: MarkupItem) => {
    if (tool === 'pen') return;
    e.stopPropagation();
    // Same focus-change cancellation as the overlay handler (see above).
    e.preventDefault();
    if (tool === 'erase') {
      pushUndo();
      setActiveItems(current => current.filter(i => i.id !== item.id));
      if (selectedId === item.id) setSelectedId(null);
      return;
    }
    if (tool === 'text' && item.kind === 'text') {
      pushUndo();
      setSelectedId(item.id);
      setEditingId(item.id);
      return;
    }
    if (tool === 'move') {
      pushUndo();
      // Text boxes get selection chrome (outline + resize handles); strokes
      // just drag.
      setSelectedId(item.kind === 'text' ? item.id : null);
      overlayRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = {
        kind: 'move',
        id: item.id,
        start: toUnits(e),
        original: item,
      };
    }
  };

  /**
   * Pointer-down on a resize handle of the selected text box: starts a
   * resize gesture. The box's current on-screen rectangle (which may be
   * auto-sized) is captured as the starting rect.
   */
  const onHandlePointerDown = (
    e: ReactPointerEvent<HTMLDivElement>,
    text: MarkupText,
    handle: ResizeHandle,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const overlayRect = overlayRef.current?.getBoundingClientRect();
    const boxRect = e.currentTarget.parentElement?.getBoundingClientRect();
    if (!overlayRect || !boxRect || overlayRect.width === 0) return;
    const toUnit = UNITS / overlayRect.width;
    overlayRef.current?.setPointerCapture(e.pointerId);
    pushUndo();
    dragRef.current = {
      kind: 'resize',
      id: text.id,
      handle,
      start: toUnits(e),
      orig: {
        x: (boxRect.left - overlayRect.left) * toUnit,
        y: (boxRect.top - overlayRect.top) * toUnit,
        w: boxRect.width * toUnit,
        h: boxRect.height * toUnit,
      },
    };
  };

  const commitTextEdit = (id: string, text: string) => {
    setEditingId(null);
    setActiveItems(current =>
      text.trim() === ''
        ? current.filter(i => i.id !== id)
        : current.map(i => (i.id === id ? {...i, text} : i)),
    );
  };

  const clearAll = () => {
    if (items.length === 0) return;
    if (!window.confirm(`Clear ALL annotations on the ${activeLayer} layer?`)) {
      return;
    }
    pushUndo();
    setActiveItems(() => []);
  };

  const switchLayer = (layer: LayerId) => {
    finishEditing();
    setActiveLayer(layer);
  };

  const selectedText: MarkupText | null = useMemo(() => {
    if (selectedId === null) return null;
    const found = items.find(i => i.id === selectedId);
    return found?.kind === 'text' ? found : null;
  }, [items, selectedId]);

  /**
   * Sets the font for the next new text box, and — when a text box is
   * currently selected — also applies it to that box immediately (mirrors
   * the slide editor's `FontFamilyControl`, which acts on the live
   * selection).
   */
  const applyFont = (family: string) => {
    setTextFont(family);
    if (selectedText === null) return;
    setActiveItems(current =>
      current.map(i => {
        if (i.id !== selectedText.id || i.kind !== 'text') return i;
        if (family === '') {
          // '' is the "Default" sentinel — drop the override entirely
          // rather than storing an empty one.
          const cleared: MarkupText = {...i};
          delete cleared.font;
          return cleared;
        }
        return {...i, font: family};
      }),
    );
  };

  /** Grows/shrinks the selected text box's font size by one step. */
  const stepTextSize = (delta: number) => {
    if (selectedText === null) return;
    setActiveItems(current =>
      current.map(i =>
        i.id === selectedText.id && i.kind === 'text'
          ? {...i, size: Math.max(6, i.size + delta)}
          : i,
      ),
    );
  };

  const pxPerUnit = pageWidth > 0 ? pageWidth / UNITS : 0;
  const heightUnits = pageWidth > 0 ? (pageHeight / pageWidth) * UNITS : 0;
  const itemsInteractive = tool !== 'pen';

  const statusTone: 'ok' | 'busy' | 'bad' =
    saveState === 'error' ? 'bad' : saveState === 'saved' ? 'ok' : 'busy';
  // Short enough to fit the pill's fixed width (see STATUS_PILL_WIDTH_PX);
  // the full explanation for 'error' lives in statusTitle's tooltip instead.
  const statusLabel = useMemo(() => {
    switch (saveState) {
      case 'loading':
        return 'Loading…';
      case 'saving':
        return 'Saving…';
      case 'dirty':
        return 'Unsaved';
      case 'error':
        return 'Save failed';
      default:
        return 'Saved to dev';
    }
  }, [saveState]);
  const statusTitle =
    saveState === 'error' ? 'Save failed — sign in as a PAI admin' : undefined;

  return (
    <Wrapper ref={wrapperRef}>
      {children}
      <Overlay
        ref={overlayRef}
        $cursor={TOOL_CURSORS[tool]}
        onPointerDown={onOverlayPointerDown}
        onPointerMove={onOverlayPointerMove}
        onPointerUp={onOverlayPointerUp}
      >
        {pageWidth > 0 && (
          <StrokeSvg
            viewBox={`0 0 ${UNITS} ${heightUnits}`}
            preserveAspectRatio="xMinYMin meet"
          >
            {items
              .filter((item): item is MarkupStroke => item.kind === 'stroke')
              .map(stroke => (
                <path
                  key={stroke.id}
                  d={strokePath(stroke.points)}
                  stroke={stroke.color}
                  strokeWidth={stroke.width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  style={{
                    pointerEvents: itemsInteractive ? 'visibleStroke' : 'none',
                    cursor: tool === 'move' ? 'grab' : undefined,
                  }}
                  onPointerDown={e => onItemPointerDown(e, stroke)}
                />
              ))}
            {liveStroke !== null && (
              <path
                d={strokePath(liveStroke.points)}
                stroke={liveStroke.color}
                strokeWidth={liveStroke.width}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                style={{pointerEvents: 'none'}}
              />
            )}
          </StrokeSvg>
        )}
        {pageWidth > 0 &&
          items
            .filter((item): item is MarkupText => item.kind === 'text')
            .map(text => {
              const sized = text.w !== undefined && text.h !== undefined;
              const frameStyle: CSSProperties = {
                left: text.x * pxPerUnit,
                top: text.y * pxPerUnit,
                ...(sized
                  ? {width: text.w * pxPerUnit, height: text.h * pxPerUnit}
                  : {}),
              };
              const contentStyle: CSSProperties = {
                fontSize: text.size * pxPerUnit,
                color: text.color,
                fontFamily: text.font || undefined,
                width: sized ? '100%' : undefined,
                height: sized ? '100%' : undefined,
              };
              const showHandles =
                tool === 'move' &&
                selectedId === text.id &&
                editingId !== text.id;

              if (editingId === text.id) {
                const lines = text.text.split('\n');
                return (
                  <TextFrame key={text.id} style={frameStyle} $selected>
                    <TextEditor
                      style={contentStyle}
                      value={text.text}
                      // Deliberate focus steal: the editor only mounts when
                      // the author just clicked to create/edit this box.
                      ref={el => el?.focus()}
                      rows={sized ? undefined : lines.length}
                      cols={
                        sized
                          ? undefined
                          : Math.max(8, ...lines.map(line => line.length + 2))
                      }
                      placeholder="Type…"
                      onChange={e =>
                        setActiveItems(current =>
                          current.map(i =>
                            i.id === text.id ? {...i, text: e.target.value} : i,
                          ),
                        )
                      }
                      onBlur={e => commitTextEdit(text.id, e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Escape') e.currentTarget.blur();
                      }}
                      onPointerDown={e => e.stopPropagation()}
                    />
                  </TextFrame>
                );
              }
              return (
                <TextFrame
                  key={text.id}
                  style={frameStyle}
                  $selected={selectedId === text.id}
                >
                  <TextItemBox
                    style={{
                      ...contentStyle,
                      cursor: tool === 'move' ? 'grab' : undefined,
                    }}
                    $interactive={itemsInteractive}
                    onPointerDown={e => onItemPointerDown(e, text)}
                  >
                    {text.text}
                  </TextItemBox>
                  {showHandles &&
                    RESIZE_HANDLES.map(handle => (
                      <HandleDot
                        key={handle}
                        style={{
                          cursor: CURSOR_BY_HANDLE[handle],
                          ...HANDLE_POSITION[handle],
                        }}
                        onPointerDown={e =>
                          onHandlePointerDown(e, text, handle)
                        }
                      />
                    ))}
                </TextFrame>
              );
            })}
      </Overlay>
      <Toolbar onPointerDown={e => e.stopPropagation()}>
        <ToolButton
          $active={activeLayer === 'desktop'}
          title="Desktop layer — annotations for wide screens"
          onClick={() => switchLayer('desktop')}
        >
          <FaDesktop />
        </ToolButton>
        <ToolButton
          $active={activeLayer === 'mobile'}
          title="Mobile layer — annotations for narrow screens"
          onClick={() => switchLayer('mobile')}
        >
          <FaMobileAlt />
        </ToolButton>
        <Divider />
        <ToolButton
          $active={tool === 'pen'}
          title="Pen — draw freehand"
          onClick={() => setTool('pen')}
        >
          <FaPen />
        </ToolButton>
        <ToolButton
          $active={tool === 'text'}
          title="Text — click the page to add a text box"
          onClick={() => setTool('text')}
        >
          <FaFont />
        </ToolButton>
        <ToolButton
          $active={tool === 'move'}
          title="Move — drag any annotation"
          onClick={() => setTool('move')}
        >
          <FaArrowsAlt />
        </ToolButton>
        <ToolButton
          $active={tool === 'erase'}
          title="Erase — click an annotation to delete it"
          onClick={() => setTool('erase')}
        >
          <FaEraser />
        </ToolButton>
        <Divider />
        {COLORS.map(c => (
          <Swatch
            key={c}
            $color={c}
            $active={color === c}
            title={c}
            onClick={() => setColor(c)}
          />
        ))}
        <Divider />
        {PEN_WIDTHS.map((w, index) => (
          <WidthDot
            key={w}
            $px={4 + index * 4}
            $active={penWidth === w}
            title={`Pen width ${w}`}
            onClick={() => setPenWidth(w)}
          />
        ))}
        {tool === 'text' && (
          <>
            <Divider />
            <FontSelect
              aria-label="Text font"
              title="Font — applies to the selected box, or the next one you create"
              value={selectedText?.font ?? textFont}
              onChange={e => applyFont(e.target.value)}
            >
              {SLIDE_FONTS.map(font => (
                <option
                  key={font.label}
                  value={font.family}
                  style={
                    font.family === '' ? undefined : {fontFamily: font.family}
                  }
                >
                  {font.label}
                </option>
              ))}
            </FontSelect>
            <SizeButton
              title="Smaller text"
              disabled={selectedText === null}
              onClick={() => stepTextSize(-TEXT_SIZE_STEP)}
            >
              A−
            </SizeButton>
            <SizeButton
              title="Larger text"
              disabled={selectedText === null}
              onClick={() => stepTextSize(TEXT_SIZE_STEP)}
            >
              A+
            </SizeButton>
          </>
        )}
        <Divider />
        <ToolButton title="Undo (Cmd/Ctrl+Z)" onClick={undo}>
          <FaUndo />
        </ToolButton>
        <ToolButton title="Clear all annotations" onClick={clearAll}>
          <FaTrash />
        </ToolButton>
        <StatusPill $tone={statusTone} title={statusTitle}>
          {statusLabel}
        </StatusPill>
      </Toolbar>
    </Wrapper>
  );
}
